import {useContext} from 'react'

import {OrderScope} from '../types'

import {OrderProcessContext, OrderProcessContextProps} from './OrderProcessContext'

/**
 * Generic hook to access common order process context.
 * Provides access to shared order data (customer, shipping address, orderId, etc.)
 * that is available across all order processes (intake, change, cancellation).
 *
 * @template TProcessData - Order data type extending OrderScope (defaults to OrderScope)
 * @returns Process context with processData
 * @throws Error if used outside of an order process provider
 *
 * @example
 * // Universal component using common data
 * function CustomerDisplay() {
 *   const { processData } = useOrderProcessContext()
 *   return (
 *     <div>
 *       <p>Customer: {processData.customer.customerName}</p>
 *       <p>Site: {processData.shippingAddress.siteName}</p>
 *     </div>
 *   )
 * }
 *
 * @example
 * // With specific type
 * function OrderIntakeSpecific() {
 *   const { processData } = useOrderProcessContext<OrderIntakeProcessData>()
 *   return <p>Type: {processData.orderIntakeType}</p>
 * }
 */
export const useOrderProcessContext = <
  TProcessData extends OrderScope = OrderScope
>(): OrderProcessContextProps<TProcessData> => {
  const context = useContext(OrderProcessContext)
  if (!context) {
    throw new Error('useOrderProcessContext must be used within an order process provider')
  }
  return context as OrderProcessContextProps<TProcessData>
}
